import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';
const CHECKOUT_URL = 'https://pay.hotmart.com/U106480894G?checkoutMode=10';
export function CTAButton({
  text,
  subtext,
  className = '',
  variant = 'primary',
  href = CHECKOUT_URL
}: {
  text: string;
  subtext?: string;
  className?: string;
  variant?: 'primary' | 'secondary' | 'outline';
  href?: string;
}) {
  const baseStyle = 'relative group overflow-hidden rounded-2xl font-extrabold flex flex-col items-center justify-center w-full md:w-auto px-9 py-4 text-white';
  const variants = {
    primary: 'bg-[length:200%_200%] bg-gradient-to-r from-amber-400 via-orange-500 to-pink-500',
    secondary: 'bg-[length:200%_200%] bg-gradient-to-r from-violet-500 via-fuchsia-500 to-cyan-400',
    outline: 'bg-transparent border border-white/20 hover:bg-white/5 !text-white'
  };
  const glowColor = {
    primary: 'rgba(249,115,22,0.65)',
    secondary: 'rgba(217,70,239,0.6)',
    outline: 'rgba(255,255,255,0)'
  }[variant];
  const isGradient = variant !== 'outline';
  return <div className="relative inline-block w-full md:w-auto" data-id="element-309">
      {isGradient && <motion.div aria-hidden className="absolute -inset-1 rounded-2xl blur-xl pointer-events-none" style={{
      background: glowColor
    }} animate={{
      opacity: [0.45, 0.85, 0.45],
      scale: [0.98, 1.03, 0.98]
    }} transition={{
      duration: 2.4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-310" />}

      <motion.a href={href} target="_blank" rel="noopener noreferrer" whileHover={{
      scale: 1.04,
      y: -2
    }} whileTap={{
      scale: 0.97
    }} animate={isGradient ? {
      backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
    } : undefined} transition={isGradient ? {
      duration: 5,
      repeat: Infinity,
      ease: 'linear'
    } : undefined} className={`${baseStyle} ${variants[variant]} ${className} relative z-10 shadow-lg`} data-id="element-311">
        <div className="flex items-center gap-2 text-lg md:text-xl relative z-20 drop-shadow-sm" data-id="element-312">
          <span data-id="element-313">{text}</span>
          <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform" data-id="element-314" />
        </div>
        {subtext && <span className="text-sm opacity-90 mt-1 font-semibold relative z-20" data-id="element-315">
            {subtext}
          </span>}

        {isGradient && <motion.div aria-hidden className="absolute top-0 -left-1/3 h-full w-1/3 -skew-x-12 bg-white/30 blur-sm pointer-events-none" animate={{
        x: ['0%', '450%']
      }} transition={{
        duration: 2.2,
        repeat: Infinity,
        ease: 'easeInOut',
        repeatDelay: 1.2
      }} data-id="element-316" />}
      </motion.a>
    </div>;
}
export function Section({
  children,
  className = '',
  id
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) {
  return <section id={id} className={`py-20 md:py-32 px-4 relative overflow-hidden ${className}`} data-id="element-317">
      <div className="container mx-auto max-w-5xl relative z-10" data-id="element-318">
        {children}
      </div>
    </section>;
}
export function GradientText({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <span className={`text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-500 ${className}`} data-id="element-319">
      {children}
    </span>;
}
export function Accordion({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-white/10 rounded-2xl bg-white/5 backdrop-blur-sm overflow-hidden mb-4" data-id="element-320">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-321">
        <span className="font-bold text-lg text-white" data-id="element-322">{title}</span>
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-323" />
      </button>
      <AnimatePresence data-id="element-324">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-325">
            <div className="px-6 pb-5 text-gray-400 leading-relaxed" data-id="element-326">
              {children}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}